import * as angular from 'angular';
import { isEmpty } from 'lodash';
import { map } from 'rxjs/operators';

import { FlamingoTypes } from '@mode/shared/contract-common';

import { EmbedActions } from '@mode/reports/sandbox/contract';
import { isBuilderMode, isDesignerMode, isLightboxMode, isReportMode } from '@mode/shared/util-js';
import { AnalyticsService } from '@mode/shared/util-segment';
import { clampElementWidthToAppleMobileBreakpoint } from '~/lib/ios-helpers';
import { DATA_LIMIT } from '../../../../common/constants';
import { VizTypes } from '../../../../common/types';
import { FeatureToggleService } from '../../../../services/feature-toggle-service';

const RENDER_TIMEOUT = 1000;

export default class ChartContainerController {
  static $inject = [
    '$state',
    '$scope',
    '$element',
    '$timeout',
    '$window',
    'ChartSanitizerService',
    'ChartInterpolaterService',
    'AnalyticsService',
    'FeatureToggleService',
  ];

  model: FlamingoTypes.ModeModel;
  viewName: string;

  dataset: any;
  view: any;
  sanitizedView: any; // need a sanitized version that can be set so diffing doesn't break in the chart components
  status: any;
  chartExists: any;
  chartId: string;
  viewer: any;
  panelEnabled: any;

  isRendering = true;
  handleOrientationChange = this.adjustForMobile.bind(this);

  onEdit: (args: { $id: string; $view: any }) => void;
  onAction: (args: { $action: EmbedActions }) => void;

  constructor(
    private $state,
    private $scope,
    private $element,
    private $timeout,
    private $window,
    private ChartSanitizerService,
    private ChartInterpolaterService,
    private as: AnalyticsService,
    private featureToggleService: FeatureToggleService
  ) {}

  $onInit() {
    // todo move this to a higher level in the chain of components and update all views when the dataset is updated, once
    this.adjustForMobile();
    this.$window.addEventListener('orientationchange', this.handleOrientationChange, false);
  }

  $onChanges(changes: angular.IOnChangesObject) {
    if (changes.view) {
      this.sanitizedView = angular.copy(this.view);
      this.handleViewUpdate();
    }
  }

  $onDestroy() {
    this.$window.removeEventListener('orientationchange', this.handleOrientationChange, false);
  }

  hasView() {
    return !isEmpty(this.view);
  }

  adjustForMobile() {
    clampElementWidthToAppleMobileBreakpoint(this.$element.children().first());
  }

  handleViewUpdate() {
    const isDesigner = this.$state.includes('application.reports.edit');
    const isSanitizeable = this.dataset && this.view && isDataContained(this.dataset) && !isDatasetLarge(this.dataset);
    const isInterpolatable = isSanitizeable && isDesigner;

    if (isSanitizeable) {
      this.ChartSanitizerService.sanitize(this.sanitizedView, this.dataset, isDesigner);
    }

    if (isInterpolatable) {
      this.ChartInterpolaterService.interpolate(this.sanitizedView, this.dataset);
    }
  }

  get drilldownEnabled$() {
    return this.featureToggleService.featureDrilldownEnabled$.pipe(
      map((isEnabled) => isEnabled && this.view && this.view.format && this.view.format.drilldownEnabled)
    );
  }

  get exploreEnabled$() {
    return this.featureToggleService.featureExploreEnabled$;
  }

  private isReportMode() {
    return isReportMode(this.$window.document.body);
  }

  private isBuilderMode() {
    return isBuilderMode(this.$window.document.body);
  }

  private isLightboxMode() {
    return isLightboxMode(this.$window.document.body);
  }

  private isDesignerMode() {
    return isDesignerMode(this.$window.document.body);
  }

  /**
   * @param opts.$vizID {string}
   */
  onRenderStart({ $vizID }) {
    if (this.isReportMode() || this.isBuilderMode()) {
      // start loading state
      this.$timeout(() => {
        this.isRendering = true;
      });
      // loading state escape hatch
      this.$timeout(() => {
        this.isRendering = false;
      }, RENDER_TIMEOUT);
      // start timing
      this.as.startVizRenderReportTiming($vizID);
    }
  }

  /**
   * @param opts.$vizID {string}
   */
  onRenderEnd({ $vizID }) {
    if (this.isDesignerMode()) {
      this.as.endVizRenderDesignerTiming($vizID, VizTypes.Chart);
    }
    if (this.isReportMode() || this.isBuilderMode()) {
      this.$timeout(() => {
        this.isRendering = false;
      });
      this.as.endVizRenderReportTiming($vizID, VizTypes.Chart);
    }
  }

  isMultiChart(selectedChart, viewVersion: number) {
    return selectedChart && ['bigNumber', 'pivotTable'].indexOf(selectedChart) < 0 && viewVersion !== 2;
  }

  isBigNumber(selectedChart) {
    return selectedChart && selectedChart === 'bigNumber';
  }

  isPivotTable(selectedChart) {
    return selectedChart && selectedChart === 'pivotTable';
  }

  queryFailed(dataset) {
    return angular.isDefined(dataset) && angular.isDefined(dataset.state) && dataset.state === 'failed';
  }
}

function isDataContained(dataset) {
  return angular.isDefined(dataset) && angular.isDefined(dataset.columns) && angular.isDefined(dataset.content);
}

function isDatasetLarge(dataset) {
  return angular.isDefined(dataset.count) && dataset.count > DATA_LIMIT;
}
